import { useEffect, useState } from "react";
import { SITE } from "../data/site";
import styles from "./AdminPage.module.css";

interface AdminRequest {
  id: string;
  receivedAt: string;
  name: string;
  contact: string;
  service: string;
  message: string;
}

interface AdminResponse {
  ok: true;
  requests: AdminRequest[];
}

const COOKIE_NAME = "cathryn_admin_session";

/** Three placeholder rows so the inbox doesn't reflow when the data lands. */
function RequestsSkeleton() {
  return (
    <ul className={styles.requests} aria-hidden="true">
      {[0, 1, 2].map((row) => (
        <li key={row} className={`${styles.request} ${styles.skeleton}`}>
          <div className={styles.meta}>
            <span className={styles.skeletonLine} style={{ width: "20%" }} />
            <span className={styles.skeletonLine} style={{ width: "30%" }} />
          </div>
          <span className={styles.skeletonLine} style={{ width: "55%", height: 16 }} />
          <span className={styles.skeletonLine} style={{ width: "85%" }} />
          <span className={styles.skeletonLine} style={{ width: "70%" }} />
        </li>
      ))}
    </ul>
  );
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = `${name}=`;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return null;
}

export function AdminPage() {
  const [password, setPassword] = useState("");
  const [session, setSession] = useState<string | null>(getCookie(COOKIE_NAME));
  const [loginError, setLoginError] = useState<string | null>(null);
  const [throttling, setThrottling] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [disabledMessage, setDisabledMessage] = useState<string | null>(null);
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    const fetchRequests = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const response = await fetch("/api/admin/booking-requests", {
          headers: { "X-CSRF-Token": getCookie("cathryn_csrf") ?? "" },
          credentials: "same-origin",
        });
        if (!response.ok) {
          throw new Error(`Server returned ${response.status}`);
        }
        const body = (await response.json()) as AdminResponse;
        if (!cancelled) setRequests(body.requests);
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : "Failed to load.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRequests();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const onLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoginError(null);
    setThrottling(false);
    try {
      const response = await fetch("/api/admin/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ password }),
      });
      if (response.status === 503) {
        const body = (await response.json().catch(() => null)) as
          | { disabled?: boolean; message?: string }
          | null;
        setDisabled(true);
        setDisabledMessage(
          body?.message ?? "Admin sign-in is disabled in this deployment.",
        );
        return;
      }
      if (response.status === 429) {
        setThrottling(true);
        return;
      }
      if (!response.ok) {
        setLoginError("Invalid password.");
        return;
      }
      const body = (await response.json()) as { ok: boolean };
      if (body.ok) {
        const cookie = getCookie(COOKIE_NAME);
        setSession(cookie);
      }
    } catch {
      setLoginError("Network error.");
    }
  };

  if (!session) {
    if (disabled) {
      return (
        <main className={`container ${styles.page}`}>
          <section className={styles.card} role="status">
            <h1 className={styles.heading}>Admin sign-in is unavailable</h1>
            <p className={styles.sub}>{disabledMessage}</p>
            <p className={styles.sub}>
              This deployment doesn&apos;t have an admin token configured, so the inbox can&apos;t
              be opened in this environment. Configure the server with{" "}
              <code>CATHRYN_ADMIN_TOKEN</code> and reload to enable sign-in.
            </p>
            <p className={styles.sub}>
              <a href="/">← Back to site</a>
            </p>
          </section>
        </main>
      );
    }
    return (
      <main className={`container ${styles.page}`}>
        <form className={styles.card} onSubmit={onLogin}>
          <h1 className={styles.heading}>Admin sign-in</h1>
          <p className={styles.sub}>
            Set <code>CATHRYN_ADMIN_TOKEN</code> in the server environment to enable this page.
          </p>
          {loginError && <div className={styles.error}>{loginError}</div>}
          {throttling && (
            <div className={styles.error}>
              Too many login attempts. Wait a few minutes and try again.
            </div>
          )}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              className={styles.input}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn btn--primary btn--lg">
            Sign in
          </button>
          <p className={styles.sub}>
            <a href="/">← Back to site</a>
          </p>
        </form>
      </main>
    );
  }

  return (
    <main className={`container ${styles.page}`}>
      <div className={styles.inbox}>
        <header className={styles.inboxHead}>
          <div>
            <h1 className={styles.heading}>Booking requests</h1>
            <p className={styles.sub}>{SITE.addressLine} · {SITE.city}</p>
          </div>
          <button
            type="button"
            className={`btn btn--ghost btn--sm ${styles.logout}`}
            onClick={async () => {
              try {
                await fetch("/api/admin/session", {
                  method: "DELETE",
                  headers: { "X-CSRF-Token": getCookie("cathryn_csrf") ?? "" },
                  credentials: "same-origin",
                });
              } catch {
                // Network failure: the cookie is HttpOnly and the user
                // can still navigate away; the server will garbage-collect
                // the session when the TTL elapses.
              }
              setSession(null);
              setRequests([]);
            }}
          >
            Sign out
          </button>
        </header>

        {loading && <RequestsSkeleton />}
        {loadError && <div className={styles.error}>{loadError}</div>}
        {!loading && !loadError && requests.length === 0 && (
          <div className={styles.empty}>
            <p className={styles.emptyHeading}>No booking requests yet.</p>
            <p className={styles.emptyBody}>
              New notes from <code>/api/booking-request</code> show up here as soon as they're
              submitted. Once a few come in, you'll see name, contact, requested service, the note
              itself, and the time it arrived.
            </p>
          </div>
        )}
        <ul className={styles.requests}>
          {requests.map((req) => (
            <li key={req.id} className={styles.request}>
              <div className={styles.meta}>
                <span>{req.service}</span>
                <span>{req.receivedAt}</span>
              </div>
              <strong>
                {req.name} · {req.contact}
              </strong>
              <p className={styles.body}>{req.message}</p>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
}
